/**
 * Feature routes — the one place every screen is registered.
 *
 * Paths match `components/layout/navigation.ts` exactly; a link in the sidebar
 * that resolves to nothing is worse than a missing link, so the two files are
 * edited together.
 *
 * Every page is lazily loaded. There are 60-odd screens and reception only ever
 * opens three of them, so shipping the inventory and reporting code to the
 * browser on first paint just delays the login screen.
 */
import { Suspense, lazy } from 'react';
import type { ComponentType, ReactElement } from 'react';
import { Route } from 'react-router-dom';
import { PERMISSIONS } from '@nova/shared';
import { ProtectedRoute } from '@/components/layout/ProtectedRoute';
import { Spinner } from '@/components/ui';

/** Suspense fallback that keeps the shell painted and only swaps the content area. */
function ScreenFallback() {
  return (
    <div className="flex h-full items-center justify-center">
      <Spinner size="lg" className="text-blue-600" />
    </div>
  );
}

/** Wrap a lazily imported page in its own Suspense boundary. */
function L(loader: () => Promise<{ default: ComponentType }>): ReactElement {
  const Component = lazy(loader);
  return (
    <Suspense fallback={<ScreenFallback />}>
      <Component />
    </Suspense>
  );
}

// --- Daily operations -------------------------------------------------------
const DashboardPage = () => import('@/features/dashboard/pages/DashboardPage');
const CalendarPage = () => import('@/features/calendar/pages/CalendarPage');
const DoctorQueuePage = () => import('@/features/doctor-queue/pages/DoctorQueuePage');
const AppointmentsListPage = () => import('@/features/appointments/pages/AppointmentsListPage');
const AppointmentClosurePage = () => import('@/features/appointments/pages/AppointmentClosurePage');

// --- Patients ---------------------------------------------------------------
const PatientsListPage = () => import('@/features/patients/pages/PatientsListPage');
const NewPatientPage = () => import('@/features/patients/pages/NewPatientPage');
const PatientDetailPage = () => import('@/features/patients/pages/PatientDetailPage');
const RecallsPage = () => import('@/features/patients/pages/RecallsPage');
const ConsentFormsPage = () => import('@/features/consents/pages/ConsentFormsPage');
const ConsentBuilderPage = () => import('@/features/consents/pages/ConsentBuilderPage');
const ConsentSubmissionsPage = () => import('@/features/consents/pages/ConsentSubmissionsPage');

// --- Clinical ---------------------------------------------------------------
const VisitsListPage = () => import('@/features/clinical/pages/VisitsListPage');
const VisitWorkspacePage = () => import('@/features/clinical/pages/VisitWorkspacePage');
const PrescriptionsPage = () => import('@/features/labs/pages/PrescriptionsPage');
const MedicationsPage = () => import('@/features/labs/pages/MedicationsPage');
const LabOrdersPage = () => import('@/features/labs/pages/LabOrdersPage');
const PatientLabResultsPage = () => import('@/features/labs/pages/PatientLabResultsPage');
const LabTestsSettingsPage = () => import('@/features/labs/pages/LabTestsSettingsPage');
const BodyMapsAdminPage = () => import('@/features/body-maps/pages/BodyMapsAdminPage');

// --- Inventory --------------------------------------------------------------
const ItemsPage = () => import('@/features/inventory/pages/ItemsPage');
const ItemDetailPage = () => import('@/features/inventory/pages/ItemDetailPage');
const SuppliersPage = () => import('@/features/inventory/pages/SuppliersPage');
const SupplierDetailPage = () => import('@/features/inventory/pages/SupplierDetailPage');
const InventoryCategoriesPage = () => import('@/features/inventory/pages/CategoriesPage');
const AttributesPage = () => import('@/features/inventory/pages/AttributesPage');
const PurchaseInvoicesPage = () => import('@/features/purchasing/pages/PurchaseInvoicesPage');
const NewPurchaseInvoicePage = () => import('@/features/purchasing/pages/NewPurchaseInvoicePage');
const PurchaseInvoiceDetailPage = () =>
  import('@/features/purchasing/pages/PurchaseInvoiceDetailPage');
const MovementsPage = () => import('@/features/stock/pages/MovementsPage');
const ConsumePage = () => import('@/features/stock/pages/ConsumePage');
const StocktakePage = () => import('@/features/stock/pages/StocktakePage');
const StockAlertsPage = () => import('@/features/stock/pages/AlertsPage');

// --- Money ------------------------------------------------------------------
const InvoicesPage = () => import('@/features/billing/pages/InvoicesPage');
const InvoiceDetailPage = () => import('@/features/billing/pages/InvoiceDetailPage');
const NewInvoicePage = () => import('@/features/billing/pages/NewInvoicePage');
const PaymentsPage = () => import('@/features/billing/pages/PaymentsPage');
const PaymentMethodsPage = () => import('@/features/billing/pages/PaymentMethodsPage');
const CommissionStatementsPage = () =>
  import('@/features/commissions/pages/CommissionStatementsPage');
const CommissionStatementDetailPage = () =>
  import('@/features/commissions/pages/CommissionStatementDetailPage');
const ExpensesPage = () => import('@/features/expenses/pages/ExpensesPage');
const ExpenseCategoriesPage = () => import('@/features/expenses/pages/ExpenseCategoriesPage');
const RecurringExpensesPage = () => import('@/features/expenses/pages/RecurringExpensesPage');
const PnlReportPage = () => import('@/features/expenses/pages/PnlReportPage');

// --- Catalogue & configuration ---------------------------------------------
const ProceduresPage = () => import('@/features/catalog/pages/ProceduresPage');
const ProcedureCategoriesPage = () => import('@/features/catalog/pages/CategoriesPage');
const CommissionRatesPage = () => import('@/features/catalog/pages/CommissionRatesPage');
const PackagesPage = () => import('@/features/packages/pages/PackagesPage');
const ClinicalFormsPage = () => import('@/features/clinical-forms/pages/ClinicalFormsPage');
const FormBuilderPage = () => import('@/features/clinical-forms/pages/FormBuilderPage');
const ClinicalSectionsPage = () => import('@/features/clinical-forms/pages/ClinicalSectionsPage');
const ClinicalSectionBuilderPage = () =>
  import('@/features/clinical-forms/pages/ClinicalSectionBuilderPage');
const TemplatesPage = () => import('@/features/messaging/pages/TemplatesPage');
const CampaignsPage = () => import('@/features/messaging/pages/CampaignsPage');
const SegmentsPage = () => import('@/features/messaging/pages/SegmentsPage');
const MessageLogPage = () => import('@/features/messaging/pages/MessageLogPage');
const AgeGroupsPage = () => import('@/features/messaging/pages/AgeGroupsPage');
const ProvidersPage = () => import('@/features/providers/pages/ProvidersPage');
const ClinicSettingsPage = () => import('@/features/settings/pages/ClinicSettingsPage');
const CalendarSettingsPage = () => import('@/features/settings/pages/CalendarSettingsPage');
const ReferenceListsPage = () => import('@/features/settings/pages/ReferenceListsPage');
const JoFotaraSettingsPage = () => import('@/features/settings/pages/JoFotaraSettingsPage');
const UsersPage = () => import('@/features/settings/pages/UsersPage');
const RolesPage = () => import('@/features/settings/pages/RolesPage');
const AuditLogPage = () => import('@/features/settings/pages/AuditLogPage');

// --- Reports ----------------------------------------------------------------
const ReportsHubPage = () => import('@/features/reports/pages/ReportsHubPage');
const ReportViewPage = () => import('@/features/reports/pages/ReportViewPage');
const ReportBuilderPage = () => import('@/features/report-builder/pages/ReportBuilderPage');
const CustomReportRunPage = () => import('@/features/report-builder/pages/CustomReportRunPage');

/**
 * Returned as a fragment of <Route> elements so App keeps one <Routes> tree.
 *
 * Routes carrying money, clinical records or administration are wrapped in a
 * permission guard as well. The sidebar already hides what a user cannot reach,
 * but a pasted URL must not open a screen the role was never granted (SEC-01).
 */
export function featureRoutes(): ReactElement {
  return (
    <>
      {/* Daily operations */}
      <Route path="/dashboard" element={L(DashboardPage)} />
      <Route path="/calendar" element={L(CalendarPage)} />
      <Route path="/queue" element={L(DoctorQueuePage)} />
      <Route path="/appointments" element={L(AppointmentsListPage)} />
      <Route path="/appointments/closure" element={L(AppointmentClosurePage)} />

      {/* Patients */}
      <Route path="/patients" element={L(PatientsListPage)} />
      <Route path="/patients/new" element={L(NewPatientPage)} />
      <Route path="/patients/recalls" element={L(RecallsPage)} />
      <Route path="/patients/:id" element={L(PatientDetailPage)} />
      <Route path="/consents" element={L(ConsentFormsPage)} />
      <Route path="/consents/submissions" element={L(ConsentSubmissionsPage)} />
      <Route path="/consents/:id" element={L(ConsentBuilderPage)} />

      {/* Clinical */}
      <Route element={<ProtectedRoute permissions={[PERMISSIONS.VISIT_VIEW]} />}>
        <Route path="/clinical/visits" element={L(VisitsListPage)} />
        <Route path="/clinical/visits/:id" element={L(VisitWorkspacePage)} />
      </Route>
      <Route path="/pharmacy/prescriptions" element={L(PrescriptionsPage)} />
      <Route path="/pharmacy/medications" element={L(MedicationsPage)} />
      <Route path="/labs" element={L(LabOrdersPage)} />
      <Route path="/labs/:patientId" element={L(PatientLabResultsPage)} />
      <Route path="/settings/lab-tests" element={L(LabTestsSettingsPage)} />
      <Route path="/settings/body-maps" element={L(BodyMapsAdminPage)} />

      {/* Inventory */}
      <Route element={<ProtectedRoute permissions={[PERMISSIONS.ITEM_VIEW]} />}>
        <Route path="/inventory/items" element={L(ItemsPage)} />
        <Route path="/inventory/items/:id" element={L(ItemDetailPage)} />
      </Route>
      <Route path="/inventory/suppliers" element={L(SuppliersPage)} />
      <Route path="/inventory/suppliers/:id" element={L(SupplierDetailPage)} />
      <Route path="/inventory/categories" element={L(InventoryCategoriesPage)} />
      <Route path="/inventory/attributes" element={L(AttributesPage)} />
      <Route element={<ProtectedRoute permissions={[PERMISSIONS.PURCHASE_VIEW]} />}>
        <Route path="/purchasing" element={L(PurchaseInvoicesPage)} />
        <Route path="/purchasing/new" element={L(NewPurchaseInvoicePage)} />
        <Route path="/purchasing/:id" element={L(PurchaseInvoiceDetailPage)} />
      </Route>
      <Route path="/stock/movements" element={L(MovementsPage)} />
      <Route path="/stock/consume" element={L(ConsumePage)} />
      <Route path="/stock/stocktake" element={L(StocktakePage)} />
      <Route path="/stock/alerts" element={L(StockAlertsPage)} />

      {/* Money */}
      <Route element={<ProtectedRoute permissions={[PERMISSIONS.INVOICE_VIEW]} />}>
        <Route path="/billing/invoices" element={L(InvoicesPage)} />
        <Route path="/billing/invoices/new" element={L(NewInvoicePage)} />
        <Route path="/billing/invoices/:id" element={L(InvoiceDetailPage)} />
        <Route path="/billing/payments" element={L(PaymentsPage)} />
      </Route>
      <Route path="/settings/payment-methods" element={L(PaymentMethodsPage)} />

      {/* COST-16 — a provider reaches their own statement through VIEW_OWN; the
          service narrows the result, so either permission may open the screen. */}
      <Route
        element={
          <ProtectedRoute
            anyOf={[
              PERMISSIONS.COMMISSION_STATEMENT_VIEW_OWN,
              PERMISSIONS.COMMISSION_STATEMENT_VIEW_ALL,
            ]}
          />
        }
      >
        <Route path="/commissions" element={L(CommissionStatementsPage)} />
        <Route path="/commissions/:id" element={L(CommissionStatementDetailPage)} />
      </Route>

      <Route element={<ProtectedRoute permissions={[PERMISSIONS.EXPENSE_VIEW]} />}>
        <Route path="/expenses" element={L(ExpensesPage)} />
        <Route path="/expenses/categories" element={L(ExpenseCategoriesPage)} />
        <Route path="/expenses/recurring" element={L(RecurringExpensesPage)} />
      </Route>
      <Route element={<ProtectedRoute permissions={[PERMISSIONS.PNL_VIEW]} />}>
        <Route path="/reports/pnl" element={L(PnlReportPage)} />
      </Route>

      {/* Catalogue & configuration */}
      <Route path="/catalog/procedures" element={L(ProceduresPage)} />
      <Route path="/catalog/categories" element={L(ProcedureCategoriesPage)} />
      <Route element={<ProtectedRoute permissions={[PERMISSIONS.PACKAGE_VIEW]} />}>
        <Route path="/catalog/packages" element={L(PackagesPage)} />
      </Route>
      <Route element={<ProtectedRoute permissions={[PERMISSIONS.COMMISSION_RATE_VIEW]} />}>
        <Route path="/catalog/commission-rates" element={L(CommissionRatesPage)} />
      </Route>
      {/* CLIN-01 — visit readers and template designers may open the builder. */}
      <Route
        element={
          <ProtectedRoute
            anyOf={[PERMISSIONS.VISIT_VIEW, PERMISSIONS.CLINICAL_TEMPLATE_MANAGE]}
          />
        }
      >
        <Route path="/clinical-forms" element={L(ClinicalFormsPage)} />
        <Route path="/clinical-forms/sections" element={L(ClinicalSectionsPage)} />
        <Route path="/clinical-forms/sections/:id" element={L(ClinicalSectionBuilderPage)} />
        <Route path="/clinical-forms/:id" element={L(FormBuilderPage)} />
      </Route>
      <Route
        element={
          <ProtectedRoute anyOf={[PERMISSIONS.MESSAGE_SEND, PERMISSIONS.TEMPLATE_MANAGE]} />
        }
      >
        <Route path="/messaging/templates" element={L(TemplatesPage)} />
      </Route>
      <Route path="/messaging/campaigns" element={L(CampaignsPage)} />
      <Route path="/messaging/segments" element={L(SegmentsPage)} />
      <Route path="/messaging/log" element={L(MessageLogPage)} />
      <Route path="/settings/age-groups" element={L(AgeGroupsPage)} />
      {/* PROV-01 — the calendar has no columns until this screen has rows. */}
      <Route element={<ProtectedRoute permissions={[PERMISSIONS.PROVIDER_VIEW]} />}>
        <Route path="/providers" element={L(ProvidersPage)} />
      </Route>

      <Route element={<ProtectedRoute permissions={[PERMISSIONS.SETTINGS_MANAGE]} />}>
        <Route path="/settings" element={L(ClinicSettingsPage)} />
        <Route path="/settings/calendar" element={L(CalendarSettingsPage)} />
        <Route path="/settings/jofotara" element={L(JoFotaraSettingsPage)} />
      </Route>
      <Route
        element={
          <ProtectedRoute
            anyOf={[PERMISSIONS.REFERENCE_LIST_MANAGE, PERMISSIONS.SETTINGS_MANAGE]}
          />
        }
      >
        <Route path="/settings/reference-lists" element={L(ReferenceListsPage)} />
      </Route>
      <Route
        element={
          <ProtectedRoute
            anyOf={[PERMISSIONS.USER_VIEW, PERMISSIONS.USER_MANAGE, PERMISSIONS.ROLE_MANAGE]}
          />
        }
      >
        <Route path="/users" element={L(UsersPage)} />
      </Route>
      <Route element={<ProtectedRoute permissions={[PERMISSIONS.ROLE_MANAGE]} />}>
        <Route path="/users/roles" element={L(RolesPage)} />
      </Route>
      <Route element={<ProtectedRoute permissions={[PERMISSIONS.AUDIT_LOG_VIEW]} />}>
        <Route path="/audit" element={L(AuditLogPage)} />
      </Route>

      {/* Reports — the hub is readable by anyone holding any report permission;
          each individual report re-checks its own family server-side. */}
      <Route path="/reports" element={L(ReportsHubPage)} />
      {/* Custom reports — static paths rank above `/reports/:reportKey`. */}
      <Route
        element={
          <ProtectedRoute
            anyOf={[PERMISSIONS.REPORT_BUILDER_VIEW, PERMISSIONS.REPORT_BUILDER_MANAGE]}
          />
        }
      >
        <Route path="/reports/builder" element={L(ReportBuilderPage)} />
        <Route path="/reports/custom/:id" element={L(CustomReportRunPage)} />
      </Route>
      <Route path="/reports/:reportKey" element={L(ReportViewPage)} />
    </>
  );
}
